"use client"
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { useEffect, useState } from "react"
import { LogOut } from "lucide-react"
import { Button } from "@/components/ui/button"
import { EnvironmentBadge } from "@/components/environment-badge"
import { usePlatformLogo } from "@/hooks/use-platform-logo"
import { useTranslations } from "next-intl"


/**
 * Full-screen warning rendered when the current tenant is archived and the
 * viewer is not a super_user. Decision #9 messaging stance: state the
 * deletion + 30-day retention factually; do NOT add a restoration CTA, a
 * "contact support" CTA, or "irreversible/permanent" language. The gate is
 * mounted SPA-side because the auth-boundary archive guard only denies new
 * tokens — an already-authenticated session needs the SPA to short-circuit.
 *
 * On mount, request server-side session revocation. The cookie cannot authorize
 * further requests after success; session polling can return the browser to sign-in.
 * Super_user is exempt because this screen never renders for that role.
 */
export function ArchivedWarningScreen({
  tenantName,
  archiveScheduledPurgeAt,
  onLogout,
  onRevokeSession,
}: {
  tenantName: string
  archiveScheduledPurgeAt: string | null | undefined
  onLogout: () => void | Promise<void>
  onRevokeSession: () => void | Promise<void>
}) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const tUi = useTranslations("Organization.ArchivedWarningScreen")

  const [signOutError, setSignOutError] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  useEffect(() => {
    let mounted = true
    void (async () => {
      try { await onRevokeSession() } catch { if (mounted) setSignOutError(true) }
    })()
    return () => { mounted = false }
  }, [onRevokeSession])

  const handleLogout = async () => {
    setSigningOut(true)
    setSignOutError(false)
    try { await onLogout() } catch { setSignOutError(true) } finally { setSigningOut(false) }
  }

  const { logoUrl: platformLogoUrl } = usePlatformLogo()

  const purgeDate = archiveScheduledPurgeAt
    ? new Date(archiveScheduledPurgeAt)
    : null
  const purgeDateLabel =
    purgeDate && !Number.isNaN(purgeDate.getTime())
      ? purgeDate.toLocaleDateString(uiFormattingLocale, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : null

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
      <div className="text-center max-w-md mx-auto p-6 space-y-6">
        <div className="flex items-center justify-center gap-2">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={platformLogoUrl}
            alt={tUi("logo")}
            className="h-10 w-auto opacity-80"
          />
          <EnvironmentBadge />
        </div>

        <div className="space-y-3">
          <h1 className="text-2xl font-semibold"><span key={tUi("organizationDeleted")}>{tUi("organizationDeleted")}</span></h1>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{tenantName}</span>{" "}
            <span><span key={tUi("isNoLongerAccessible")}>{tUi("isNoLongerAccessible")}</span></span>
          </p>
          {purgeDateLabel && (
            <p className="text-sm text-muted-foreground">
              <span><span key={tUi("dataIsRetainedUntil")}>{tUi("dataIsRetainedUntil")}</span></span> {purgeDateLabel}<span>.</span>
            </p>
          )}
        </div>

        {signOutError && <p role="alert" className="text-sm text-destructive"><span key={tUi("couldNotSignOutPleaseTryAgain")}>{tUi("couldNotSignOutPleaseTryAgain")}</span></p>}
        <Button variant="outline" onClick={() => void handleLogout()} disabled={signingOut} className="w-full">
          <LogOut className="w-4 h-4 mr-2" />
          <span><span key={tUi("signOut")}>{tUi("signOut")}</span></span>
        </Button>
      </div>
    </div>
  )
}
